import { memo } from "react";
import { useTranslations } from "next-intl";
import { IconCheck, IconCopy, IconRefresh } from "@tabler/icons-react";
import { HoverInfo } from "@/components/HoverInfo/HoverInfo";
import { useCopyToClipboard } from "./useCopyToClipboard";

/**
 * Hover/focus action row under an assistant bubble: Copy (raw content) and,
 * on the last assistant turn only, Regenerate (→ retryLast). Revealed on
 * group-hover / focus-within; keyboard reachable. `onRegenerate` must be a
 * stable reference (provider's retryLast) to keep the bubble memoized (§1a).
 */
export const MessageActions = memo(function MessageActions({
  content,
  canRegenerate,
  onRegenerate,
}: {
  content: string;
  canRegenerate: boolean;
  onRegenerate: () => void;
}) {
  const t = useTranslations("aiAssistant");
  const { copied, copy } = useCopyToClipboard();
  return (
    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
      <HoverInfo message={copied ? t("copied") : t("copyMessage")}>
        <button
          type="button"
          data-testid="ai-msg-copy"
          aria-label={t("copyMessage")}
          className="rounded-md p-1 font-tertiary hover:surface-secondary transition-colors cursor-pointer focus-ring"
          onClick={() => copy(content)}
        >
          {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
        </button>
      </HoverInfo>
      {canRegenerate && (
        <HoverInfo message={t("regenerate")}>
          <button
            type="button"
            data-testid="ai-msg-regenerate"
            aria-label={t("regenerate")}
            className="rounded-md p-1 font-tertiary hover:surface-secondary transition-colors cursor-pointer focus-ring"
            onClick={onRegenerate}
          >
            <IconRefresh size={14} />
          </button>
        </HoverInfo>
      )}
    </div>
  );
});
